import React from 'react';
import { useHistory, useLocation, useParams, useRouteMatch } from 'react-router-dom';
import { css } from '@emotion/react';
import { PanelContainer } from '../../components/panel';
import api from '../../api';
import { color } from '../../styles';
import { LAXATIVE_TYPE_JA, LAXATIVE_UNIT_JA, LAXATIVE_UNIT_EN, LAXATIVE_TYPE_EN } from '../../constants';
import { IGetLaxativeRes } from '../../types/api/care-settings/getLaxative';
import { IGetDrinkRes } from '../../types/api/care-settings/getDrink';
import { IGetDiaperRes } from '../../types/api/care-settings/getDiaper';
import { OutlineButton } from '../../components/outlineButton';
import { LaxativeTable } from '../../components/parts/laxativeTable';
import { DiaperTable } from '../../components/parts/diaperTable';
import { DrinkTable } from '../../components/parts/drinkTable';
import i18n from '@/i18n/configs';
import { useTranslation } from 'react-i18next';
import { UnitSelector } from './stocks/unitSelector';
import { MenuItemProps } from '../../components/mui/select';
import { useFetchDiapersRequired } from '@/hooks/api/diaperRequired/useFetchDiaperRequired';
import { useFetchMasterDiapers } from '@/hooks/api/master/useFetchMasterDiapers';
import { useSelector } from 'react-redux';
import { selfDataSelector, showMedicalContentSelector } from '@/redux/config/selfData';
import { useSnackbar } from '@/contexts/SnackbarContext';

export interface IGetLaxative extends IGetLaxativeRes {
  displayUnit: string; // 日本語化された下剤の単位
  displayType: string; // 日本語化された下剤のタイプ
}

const TableTitle = css`
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 36px;
  margin-bottom: 8px;

  p {
    font-size: 16px;
    font-weight: bold;
    color: ${color.dark_gray};
  }
`;

const TableContainer = css`
  margin-bottom: 24px;
`;

export const ExpendableList = () => {
  const { t, i18n } = useTranslation();
  const { nursingHomeId } = useParams<{ nursingHomeId: string }>();
  const { url } = useRouteMatch();
  const { hash } = useLocation();
  const { selfUnits, selfUser } = useSelector(selfDataSelector);
  const showMedicalContent = useSelector(showMedicalContentSelector);
  const [laxatives, setLaxatives] = React.useState<IGetLaxative[] | null>(null);
  const [drinks, setDrinks] = React.useState<IGetDrinkRes[] | null>(null);
  const [diapers, setDiapers] = React.useState<IGetDiaperRes[] | null>(null);
  const [selectedUnitId, setSelectedUnitId] = React.useState<number>(0);
  const history = useHistory();
  const { diapersRequired, fetchDiapersRequired } = useFetchDiapersRequired(true);
  const { masterDiapers } = useFetchMasterDiapers();
  const { showSnackbar } = useSnackbar();
  const tapePantsRef = React.useRef<HTMLDivElement>(null);
  const isListLoaded = laxatives !== null && drinks !== null && diapers !== null;

  React.useEffect(() => {
    if (hash !== '#tape-pants' || !isListLoaded) return;

    // 一覧の描画でスクロール位置・範囲が変わるため、データ取得後に移動する。
    tapePantsRef.current?.scrollIntoView();
  }, [hash, isListLoaded, i18n.language]);

  const requiredMap = React.useMemo(() => {
    const map = new Map<number, { id: number; requiredStock: number; borderLine: number }>();
    if (diapersRequired && diapersRequired.length) {
      for (const v of diapersRequired as any[]) {
        if (!map.has(v.diaperId)) {
          map.set(v.diaperId, { id: v.id, requiredStock: v.requiredStock, borderLine: v.borderLine });
        }
      }
    }
    return map;
  }, [diapersRequired]);

  const bagsPerCaseMap = React.useMemo(() => {
    const map = new Map<number, number>();
    if (masterDiapers && masterDiapers.length) {
      masterDiapers.forEach((m: any) => {
        map.set(m.id, m.bags ?? 0);
      });
    }
    return map;
  }, [masterDiapers]);

  const diapersWithRequired = React.useMemo(() => {
    if (!diapers) return null;
    const currentUnitId =
      selectedUnitId && selectedUnitId > 0
        ? selectedUnitId
        : selfUser?.hasUnitPerm && selfUser?.nursingHomeUnitId
        ? selfUser.nursingHomeUnitId
        : 0;
    const currentUnitName =
      currentUnitId === 0 ? '施設全体' : selfUnits?.find((u: any) => u.id === currentUnitId)?.name ?? '';
    return diapers.map((d) => {
      const req = requiredMap.get(d.diaperId);
      const bagsPerCase = bagsPerCaseMap.get(d.diaperId);
      let caseRequiredStock: number | null = null;
      let bagRequiredStock: number | null = null;
      let caseBorderLine: number | null = null;
      let bagBorderLine: number | null = null;
      if (req) {
        if (bagsPerCase && bagsPerCase > 0) {
          caseRequiredStock = Math.floor(req.requiredStock / bagsPerCase);
          bagRequiredStock = req.requiredStock % bagsPerCase;
          caseBorderLine = Math.floor(req.borderLine / bagsPerCase);
          bagBorderLine = req.borderLine % bagsPerCase;
        } else {
          caseRequiredStock = 0;
          bagRequiredStock = req.requiredStock;
          caseBorderLine = 0;
          bagBorderLine = req.borderLine;
        }
      }
      return {
        ...d,
        unitName: currentUnitName,
        bagsPerCase,
        caseRequiredStock,
        bagRequiredStock,
        caseBorderLine,
        bagBorderLine,
      };
    });
  }, [diapers, requiredMap, bagsPerCaseMap, selectedUnitId, selfUnits, selfUser]);

  const pants = React.useMemo(
    () => (diapersWithRequired ? diapersWithRequired.filter((d) => d.diaperShape === 'outer') : null),
    [diapersWithRequired]
  );
  const pads = React.useMemo(
    () => (diapersWithRequired ? diapersWithRequired.filter((d) => d.diaperShape === 'inner') : null),
    [diapersWithRequired]
  );

  const fetchLaxatives = async () => {
    const laxatives = await api.get('/nursing-homes/:nursingHomeId/laxatives', {
      params: { nursingHomeId },
    });
    const formattedLaxatives = laxatives.map((laxative: IGetLaxativeRes) => {
      return {
        ...laxative,
        displayUnit: i18n.language.startsWith('ja') ? LAXATIVE_UNIT_JA[laxative.unit] : LAXATIVE_UNIT_EN[laxative.unit],
        displayType: i18n.language.startsWith('ja') ? LAXATIVE_TYPE_JA[laxative.type] : LAXATIVE_TYPE_EN[laxative.type],
      };
    });
    setLaxatives(formattedLaxatives);
  };

  const fetchDrinks = async () => {
    const drinks = await api.get('/nursing-homes/:nursingHomeId/drinks', {
      params: { nursingHomeId },
    });
    setDrinks(drinks);
  };

  const fetchDiaper = async () => {
    const diapers = await api.get('/nursing-homes/:nursingHomeId/diapers', {
      params: { nursingHomeId, sort: 'desc', limit: 10000 },
    });
    setDiapers(diapers);
  };

  // type = diaper | padがくる  diaperShape = outer | innerがくる
  const handleOnClickAddButton = (type: string, diaperShape?: string) => () => {
    if (diaperShape) {
      history.push(`${url}/${type}-${diaperShape}/new`);
    } else {
      history.push(`${url}/${type}/new`);
    }
  };

  React.useEffect(() => {
    fetchLaxatives();
    fetchDrinks();
    fetchDiaper();
  }, []);

  const handleChangeUnit = (e: MenuItemProps) => {
    const unitId = +e.key;
    setSelectedUnitId(unitId);
    fetchDiapersRequired(unitId, 'desc', 10000);
  };

  const handleUpdateRequiredStock = async (
    diaper: any,
    values: { caseRequiredStock: number; bagRequiredStock: number; caseBorderLine: number; bagBorderLine: number }
  ) => {
    try {
      const bagsPerCase = bagsPerCaseMap.get(diaper.diaperId) ?? 0;
      const requiredStock = (values.caseRequiredStock ?? 0) * bagsPerCase + (values.bagRequiredStock ?? 0);
      const borderLine = (values.caseBorderLine ?? 0) * bagsPerCase + (values.bagBorderLine ?? 0);
      if (requiredStock < borderLine) {
        showSnackbar('発注ラインをあるべき在庫数以下にしてください', 'error');
        return;
      }
      const unitId =
        selectedUnitId && selectedUnitId > 0
          ? selectedUnitId
          : selfUser?.hasUnitPerm && selfUser?.nursingHomeUnitId
          ? selfUser.nursingHomeUnitId
          : null;
      const matchedRequired = requiredMap.get(diaper.diaperId);
      // 既存と同一値の場合は更新不要
      if (
        matchedRequired &&
        matchedRequired.requiredStock === requiredStock &&
        matchedRequired.borderLine === borderLine
      ) {
        return;
      }
      // 新規作成かつ、更新内容が0の場合はスキップ
      if (!matchedRequired && requiredStock === 0 && borderLine === 0) {
        return;
      }
      if (matchedRequired) {
        await api.put('/diaper-required/:diaperRequiredId', {
          diaperRequiredId: matchedRequired.id,
          nursingHomeDiaperId: diaper.diaperId,
          nursingHomeUnitId: unitId,
          requiredStock,
          borderLine,
        });
      } else {
        await api.post('/nursing-homes/:nursingHomeId/diaper-required', {
          nursingHomeId,
          nursingHomeDiaperId: diaper.diaperId,
          nursingHomeUnitId: unitId,
          requiredStock,
          borderLine,
        });
      }
      showSnackbar('保存しました', 'success');

      const refetchUnit = unitId === null ? null : +unitId;
      await fetchDiapersRequired(refetchUnit, 'desc', 10000);
    } catch (e) {
      showSnackbar('エラーが発生しました', 'error');
    }
  };

  return (
    <PanelContainer title={t('registrationOfLaxativesAndDrinks', '下剤・消耗品の登録')}>
      <div css={TableTitle}>
        <p>{t('laxatives', '下剤')}</p>
        <OutlineButton icon={'╋'} onClick={handleOnClickAddButton('laxative')}>
          {t('add', '追加')}
        </OutlineButton>
      </div>
      <div css={TableContainer}>
        {laxatives && laxatives.length ? (
          <LaxativeTable data={laxatives} url={url} dataFetch={fetchLaxatives} />
        ) : (
          <>{t('noLaxativesRegistered', '下剤が登録されていません')}</>
        )}
      </div>
      <div css={TableTitle}>
        <p>{t('drinks', 'ドリンク')}</p>
        <OutlineButton icon={'╋'} onClick={handleOnClickAddButton('drink')}>
          {t('add', '追加')}
        </OutlineButton>
      </div>
      <div css={TableContainer}>
        {drinks && drinks.length ? (
          <DrinkTable data={drinks} url={url} dataFetch={fetchDrinks} />
        ) : (
          <>{t('noDrinksRegistered', 'ドリンクが登録されていません')}</>
        )}
      </div>
      {!showMedicalContent && i18n.language.startsWith('ja') && (
        <>
          <UnitSelector onChangeUnit={handleChangeUnit} />
          <div id='tape-pants' ref={tapePantsRef} css={TableTitle}>
            <p>テープ/パンツ</p>
            <OutlineButton icon={'╋'} onClick={handleOnClickAddButton('diaper', 'outer')}>
              追加
            </OutlineButton>
          </div>
          <div css={TableContainer}>
            {pants && pants.length ? (
              <DiaperTable
                diapers={pants}
                type='outer'
                url={url}
                dataFetch={fetchDiaper}
                onUpdate={handleUpdateRequiredStock}
              />
            ) : (
              <>{'テープ/パンツが登録されていません'}</>
            )}
          </div>
          <div css={TableTitle}>
            <p>パッド</p>
            <OutlineButton icon={'╋'} onClick={handleOnClickAddButton('diaper', 'inner')}>
              追加
            </OutlineButton>
          </div>
          <div css={TableContainer}>
            {pads && pads.length ? (
              <DiaperTable
                diapers={pads}
                type='inner'
                url={url}
                dataFetch={fetchDiaper}
                onUpdate={handleUpdateRequiredStock}
              />
            ) : (
              <>{'パッドが登録されていません'}</>
            )}
          </div>
        </>
      )}
    </PanelContainer>
  );
};
